import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  CalendarClock,
  Check,
  CheckCircle2,
  Clock,
  Pencil,
  Plus,
  Search,
  Trash2,
  Truck,
  Wrench,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { maintenanceApi } from '@/api/endpoints/maintenance';
import { permissionsApi } from '@/api/endpoints/permissions';
import { ApiError } from '@/api/client';
import { useFleetStore } from '@/stores/fleet';
import { cn } from '@/lib/utils';
import type { Maintenance } from '@/api/types';

interface TypeSpec {
  key: string;
  label: string;
  description: string;
  icon: typeof Clock;
}

const TYPES: TypeSpec[] = [
  {
    key: 'hours',
    label: 'Horas trabalhadas',
    description: 'Alerta a cada N horas de operação',
    icon: Clock,
  },
  {
    key: 'engineHours',
    label: 'Horas de motor',
    description: 'Alerta a cada N horas de motor ligado',
    icon: Clock,
  },
];

function specForType(type?: string): TypeSpec {
  return TYPES.find((t) => t.key === type) ?? TYPES[0];
}

const HOURS_MS = 3_600_000;
const DAY_MS = 24 * 3_600_000;

interface ScheduleAttrs {
  scheduleDate?: string;
  recurrenceDays?: number;
  deviceId?: number;
}

function readAttrs(m: Maintenance): ScheduleAttrs {
  const a = m.attributes ?? {};
  return {
    scheduleDate: typeof a.scheduleDate === 'string' ? (a.scheduleDate as string) : undefined,
    recurrenceDays: typeof a.recurrenceDays === 'number' ? (a.recurrenceDays as number) : undefined,
    deviceId: typeof a.deviceId === 'number' ? (a.deviceId as number) : undefined,
  };
}

function scheduleStatus(date?: string): 'vencida' | 'proxima' | 'ok' | null {
  if (!date) return null;
  const target = new Date(`${date}T23:59:59`);
  const diffDays = Math.floor((target.getTime() - Date.now()) / DAY_MS);
  if (diffDays < 0) return 'vencida';
  if (diffDays <= 7) return 'proxima';
  return 'ok';
}

export function MaintenancePage() {
  const list = useQuery({
    queryKey: ['maintenance'],
    queryFn: () => maintenanceApi.list({ all: true }),
  });
  const [editing, setEditing] = useState<Maintenance | 'new' | null>(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const data = list.data ?? [];
    if (!q) return data;
    return data.filter(
      (m) => m.name?.toLowerCase().includes(q) || specForType(m.type).label.toLowerCase().includes(q),
    );
  }, [list.data, search]);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Manutenção</h1>
          <p className="text-sm text-muted-foreground">
            Alertas por máquina baseados em horas trabalhadas ou horas de motor. Para lembretes
            por data, use a <a href="/agenda" className="text-primary hover:underline">Agenda</a>.
          </p>
        </div>
        <Button onClick={() => setEditing('new')} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Nova manutenção
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou tipo"
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      {list.isLoading ? (
        <p className="p-6 text-sm text-muted-foreground">Carregando manutenções…</p>
      ) : list.error ? (
        <p className="p-6 text-sm text-destructive">Erro ao carregar manutenções.</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Wrench className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {search
                ? 'Nenhuma manutenção corresponde à busca.'
                : 'Nenhuma manutenção cadastrada.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((m) => (
            <MaintenanceCard key={m.id} maintenance={m} onEdit={() => setEditing(m)} />
          ))}
        </div>
      )}

      {editing && (
        <MaintenanceEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function MaintenanceCard({
  maintenance,
  onEdit,
}: {
  maintenance: Maintenance;
  onEdit: () => void;
}) {
  const spec = specForType(maintenance.type);
  const Icon = spec.icon;
  const attrs = readAttrs(maintenance);
  const devices = useFleetStore((s) => s.devices);
  const device = attrs.deviceId ? devices.get(attrs.deviceId) : undefined;
  const status = spec.key === 'schedule' ? scheduleStatus(attrs.scheduleDate) : null;

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                status === 'vencida'
                  ? 'bg-status-blocked/10 text-status-blocked'
                  : status === 'proxima'
                    ? 'bg-status-ready/10 text-status-ready'
                    : 'bg-status-running/10 text-status-running',
              )}
            >
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold">{maintenance.name}</p>
              <p className="truncate text-xs text-muted-foreground">{spec.label}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
            aria-label="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>

        {device ? (
          <p className="flex items-center gap-1.5 text-xs">
            <Truck className="h-3.5 w-3.5 text-primary" />
            <span className="truncate font-semibold">{device.name}</span>
          </p>
        ) : attrs.deviceId ? (
          <p className="text-xs text-muted-foreground">Máquina ID {attrs.deviceId}</p>
        ) : (
          <p className="text-xs text-muted-foreground">Sem máquina vinculada</p>
        )}

        {spec.key === 'schedule' ? (
          <ScheduleInfo attrs={attrs} status={status} />
        ) : (
          <dl className="grid grid-cols-2 gap-1 text-xs">
            <div>
              <dt className="text-muted-foreground">Início</dt>
              <dd className="font-semibold">
                {((maintenance.start ?? 0) / HOURS_MS).toLocaleString('pt-BR', {
                  maximumFractionDigits: 2,
                })}{' '}
                h
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">A cada</dt>
              <dd className="font-semibold">
                {((maintenance.period ?? 0) / HOURS_MS).toLocaleString('pt-BR', {
                  maximumFractionDigits: 2,
                })}{' '}
                h
              </dd>
            </div>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function ScheduleInfo({
  attrs,
  status,
}: {
  attrs: ScheduleAttrs;
  status: ReturnType<typeof scheduleStatus>;
}) {
  const StatusIcon = status === 'vencida' ? AlertTriangle : status === 'proxima' ? Clock : CheckCircle2;
  const label =
    status === 'vencida' ? 'Vencida' : status === 'proxima' ? 'Próxima' : status === 'ok' ? 'No prazo' : '—';
  const color =
    status === 'vencida'
      ? 'text-status-blocked'
      : status === 'proxima'
        ? 'text-status-ready'
        : 'text-status-running';

  return (
    <div className="space-y-1 text-xs">
      <p className="flex items-center gap-1.5">
        <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-semibold">
          {attrs.scheduleDate
            ? new Date(`${attrs.scheduleDate}T00:00:00`).toLocaleDateString('pt-BR')
            : '—'}
        </span>
        {status && (
          <span className={cn('inline-flex items-center gap-0.5 font-bold', color)}>
            <StatusIcon className="h-3 w-3" />
            {label}
          </span>
        )}
      </p>
      {attrs.recurrenceDays && attrs.recurrenceDays > 0 && (
        <p className="text-muted-foreground">A cada {attrs.recurrenceDays} dias</p>
      )}
    </div>
  );
}

interface FormState {
  name: string;
  typeKey: string;
  deviceId: number | null;
  hoursStart: string;
  hoursPeriod: string;
  scheduleDate: string;
  recurrenceDays: string;
}

function fromMaintenance(m: Maintenance): FormState {
  const spec = specForType(m.type);
  const attrs = readAttrs(m);
  return {
    name: m.name ?? '',
    typeKey: spec.key,
    deviceId: attrs.deviceId ?? null,
    hoursStart: ((m.start ?? 0) / HOURS_MS).toString(),
    hoursPeriod: ((m.period ?? 0) / HOURS_MS).toString(),
    scheduleDate: attrs.scheduleDate ?? '',
    recurrenceDays: attrs.recurrenceDays?.toString() ?? '',
  };
}

const TODAY_ISO = new Date().toISOString().slice(0, 10);

function MaintenanceEditor({
  initial,
  onClose,
}: {
  initial: Maintenance | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const devices = useFleetStore((s) => s.devices);
  const sortedDevices = useMemo(
    () => Array.from(devices.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [devices],
  );

  const [form, setForm] = useState<FormState>(
    initial
      ? fromMaintenance(initial)
      : {
          name: '',
          typeKey: 'hours',
          deviceId: null,
          hoursStart: '0',
          hoursPeriod: '250',
          scheduleDate: TODAY_ISO,
          recurrenceDays: '',
        },
  );
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (initial) setForm(fromMaintenance(initial));
  }, [initial]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const isSchedule = form.typeKey === 'schedule';

  const errors = useMemo(() => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) e.name = 'Obrigatório';
    if (!form.deviceId) e.deviceId = 'Escolha a máquina';
    if (isSchedule) {
      if (!form.scheduleDate) e.scheduleDate = 'Obrigatório';
    } else {
      const periodNum = Number(form.hoursPeriod);
      if (!Number.isFinite(periodNum) || periodNum <= 0) e.hoursPeriod = 'Maior que zero';
      const startNum = Number(form.hoursStart);
      if (!Number.isFinite(startNum) || startNum < 0) e.hoursStart = 'Maior ou igual a zero';
    }
    return e;
  }, [form, isSchedule]);

  const valid = Object.keys(errors).length === 0;

  const buildPayload = (): Maintenance => {
    const baseAttrs = (initial?.attributes ?? {}) as Record<string, unknown>;
    const attributes: Record<string, unknown> = {
      ...baseAttrs,
      deviceId: form.deviceId,
    };
    let start = 0;
    let period = 0;

    if (isSchedule) {
      attributes.scheduleDate = form.scheduleDate;
      const r = Number(form.recurrenceDays);
      if (Number.isFinite(r) && r > 0) attributes.recurrenceDays = r;
      else delete attributes.recurrenceDays;
    } else {
      delete attributes.scheduleDate;
      delete attributes.recurrenceDays;
      start = Number(form.hoursStart) * HOURS_MS;
      period = Number(form.hoursPeriod) * HOURS_MS;
    }

    return {
      ...(initial ?? { id: 0 }),
      name: form.name.trim(),
      type: form.typeKey,
      start,
      period,
      attributes,
    } as Maintenance;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      let saved: Maintenance;
      if (initial) {
        saved = await maintenanceApi.update(initial.id, payload);
      } else {
        const { id, ...rest } = payload;
        void id;
        saved = await maintenanceApi.create(rest);
      }

      if (saved.id && form.deviceId) {
        try {
          await permissionsApi.link({ maintenanceId: saved.id, deviceId: form.deviceId });
        } catch (e) {
          console.warn('Vínculo manutenção/máquina:', e);
        }
      }
      return saved;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => maintenanceApi.delete(initial!.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      onClose();
    },
  });

  const errorText = (e: unknown) => {
    if (e instanceof ApiError) return `Servidor respondeu ${e.status} (${e.statusText || 'erro'}).`;
    if (e instanceof Error) return e.message;
    return 'Erro desconhecido.';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    saveMutation.mutate();
  };

  const onConfirmDelete = () => {
    if (!initial) return;
    if (!window.confirm(`Excluir a manutenção "${initial.name}"?`)) return;
    deleteMutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-2 sm:p-6"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="flex max-h-[95vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-bold">
            {initial ? 'Editar manutenção' : 'Nova manutenção'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <Field label="Nome" required error={touched ? errors.name : undefined}>
            <Input
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Ex: Troca de óleo / Limpeza filtro / Calibração"
              autoFocus
            />
          </Field>

          <Field label="Máquina" required error={touched ? errors.deviceId : undefined}>
            <select
              value={form.deviceId ?? ''}
              onChange={(e) => update('deviceId', e.target.value ? Number(e.target.value) : null)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Selecione…</option>
              {sortedDevices.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.model ? ` · ${d.model}` : ''}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Tipo de medição">
            <div className="grid grid-cols-3 gap-1.5">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => update('typeKey', t.key)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-[11px] font-semibold transition-colors',
                    form.typeKey === t.key
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-accent',
                  )}
                >
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">
              {specForType(form.typeKey).description}
            </p>
          </Field>

          {!isSchedule && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Início (h)" error={touched ? errors.hoursStart : undefined}>
                <Input
                  type="number"
                  min={0}
                  step="any"
                  value={form.hoursStart}
                  onChange={(e) => update('hoursStart', e.target.value)}
                />
              </Field>
              <Field label="A cada (h)" required error={touched ? errors.hoursPeriod : undefined}>
                <Input
                  type="number"
                  min={0}
                  step="any"
                  value={form.hoursPeriod}
                  onChange={(e) => update('hoursPeriod', e.target.value)}
                />
              </Field>
            </div>
          )}

          {isSchedule && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Próxima data" required error={touched ? errors.scheduleDate : undefined}>
                <Input
                  type="date"
                  value={form.scheduleDate}
                  onChange={(e) => update('scheduleDate', e.target.value)}
                />
              </Field>
              <Field label="Recorrência (dias)">
                <Input
                  type="number"
                  min={0}
                  value={form.recurrenceDays}
                  onChange={(e) => update('recurrenceDays', e.target.value)}
                  placeholder="Opcional"
                />
              </Field>
            </div>
          )}

          {isSchedule && (
            <p className="rounded-md bg-status-ready/10 px-3 py-2 text-[11px] text-status-ready">
              Manutenções por data são lembretes do painel. O servidor não dispara evento automático
              porque o Traccar só monitora atributos numéricos crescentes. Status Vencida/Próxima/No prazo
              é calculado pelo frontend.
            </p>
          )}

          {(saveMutation.error || deleteMutation.error) && (
            <p className="flex items-start gap-2 rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {errorText(saveMutation.error ?? deleteMutation.error)}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
          {initial && (
            <Button
              type="button"
              variant="destructive"
              onClick={onConfirmDelete}
              disabled={deleteMutation.isPending || saveMutation.isPending}
              className="mr-auto gap-1.5"
            >
              <Trash2 className="h-4 w-4" />
              {deleteMutation.isPending ? 'Excluindo…' : 'Excluir'}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!valid || saveMutation.isPending} className="gap-1.5">
            <Check className="h-4 w-4" />
            {saveMutation.isPending ? 'Salvando…' : initial ? 'Salvar' : 'Criar'}
          </Button>
        </footer>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>
          {label}
          {required && <span className="text-status-blocked"> *</span>}
        </span>
        {error && <span className="text-status-blocked">{error}</span>}
      </span>
      <div className={cn(error && 'rounded-md ring-2 ring-status-blocked/30')}>{children}</div>
    </label>
  );
}
